import React from 'react';
import { Link } from 'react-router-dom';
import ThemeToggle from '../components/ThemeToggle';
import BackToTop from '../components/BackToTop';
import FeatureSection from '../components/landing/FeatureSection';
import PricingTiers from '../components/landing/PricingTiers';
import TickerTape from '../components/landing/TickerTape';
import HeroDemoCard from '../components/landing/HeroDemoCard';
import { NEW_SIGNUPS_OPEN, PRICING_TIERS, planNamesWith, tiersWith } from '../data/pricingTiers';

// Feature copy that quotes plan limits, from the one plan source
const TOP_PLAN = PRICING_TIERS[PRICING_TIERS.length - 1];
const FIRST_AI_PLAN = tiersWith('aiAnalysis')[0];
const AI_USAGE_RANGE =
  `${FIRST_AI_PLAN.limits.aiAnalysis}/${FIRST_AI_PLAN.limits.period} (${FIRST_AI_PLAN.shortName})` +
  ` → ${TOP_PLAN.limits.aiAnalysis}/${TOP_PLAN.limits.period} (${TOP_PLAN.shortName})`;

const Landing: React.FC = () => {

  const scrollToPricing = (e: React.MouseEvent) => {
    e.preventDefault();
    const el = document.getElementById('pricing');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-emerald-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      {/* SEO — React 19 hoists these to <head> automatically */}
      <title>NWC-Analytics — Professional Stock Analysis for Retail Investors</title>
      <meta name="description" content="Real-time prices, AI-powered stock & portfolio analysis, 15+ technical indicators, DCF & relative valuation, and smart alerts — built for retail investors who do their own homework." />
      <link rel="canonical" href="https://nwc-analytics.com/" />
      <meta property="og:type" content="website" />
      <meta property="og:title" content="NWC-Analytics — Professional Stock Analysis for Retail Investors" />
      <meta property="og:description" content="Real-time prices, AI analysis, technical alerts, DCF modeling — for retail investors who do their own homework." />
      <meta property="og:url" content="https://nwc-analytics.com" />
      <meta property="og:image" content="https://nwc-analytics.com/images/og-default.png" />
      <meta property="og:site_name" content="NWC-Analytics" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content="NWC-Analytics — Professional Stock Analysis" />
      <meta name="twitter:description" content="Institutional-grade stock analysis tools built for retail investors." />
      <meta name="twitter:image" content="https://nwc-analytics.com/images/og-default.png" />

      {/* JSON-LD: Organization + WebSite (valid anywhere in DOM for Google) */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@graph": [
          {
            "@type": "Organization",
            "name": "NWC-Analytics, LLC",
            "url": "https://nwc-analytics.com",
            "logo": "https://nwc-analytics.com/images/logo.png",
            "description": "Professional stock analysis platform for retail investors.",
            "address": {
              "@type": "PostalAddress",
              "addressLocality": "Post Falls",
              "addressRegion": "ID",
              "addressCountry": "US"
            }
          },
          {
            "@type": "WebSite",
            "name": "NWC-Analytics",
            "url": "https://nwc-analytics.com",
            "description": "Real-time market data, AI analysis, technical indicators, DCF valuations, and smart alerts for retail investors.",
            "publisher": { "@type": "Organization", "name": "NWC-Analytics, LLC" }
          }
        ]
      }) }} />

      {/* Navbar */}
      <nav className="bg-gray-800 dark:bg-gray-900 shadow-sm border-b border-gray-700 dark:border-gray-700 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              <img
                src="/images/logo.png"
                alt="NWC-Analytics, LLC"
                className="h-12 w-12 mr-3"
              />
              <span className="text-xl font-bold text-primary-400 dark:text-primary-400" style={{ fontFamily: "'Viner Hand ITC', 'Caveat', cursive", fontSize: '1.8rem', fontStyle: 'italic' }}>NWC-Analytics</span>
            </div>
            <div className="flex items-center space-x-4">
              <Link
                to="/blogs"
                className="text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 px-3 py-2 rounded-md text-sm font-medium transition-colors"
              >
                Blog
              </Link>
              <Link
                to="/login"
                className="text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 px-3 py-2 rounded-md text-sm font-medium transition-colors"
              >
                Sign In
              </Link>
              {NEW_SIGNUPS_OPEN && (
                <button
                  onClick={scrollToPricing}
                  className="bg-primary-600 hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-600 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors shadow-sm cursor-pointer"
                >
                  Get Started
                </button>
              )}
              <ThemeToggle />
            </div>
          </div>
        </div>
      </nav>

      {/* Live ticker tape (volume-sorted most-actives) */}
      <TickerTape />
      {/* Screener tape: High Volume Breakout + Volatility Squeeze quick-screens */}
      <TickerTape source="screens" />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0 -z-10 opacity-40 dark:opacity-30"
          style={{
            background:
              'radial-gradient(60% 50% at 30% 20%, rgba(13,148,136,0.25) 0%, transparent 60%), radial-gradient(40% 40% at 80% 70%, rgba(13,148,136,0.18) 0%, transparent 60%)',
          }}
        />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] text-gray-900 dark:text-white">
              Wall Street's research tools.
              <span className="block text-primary-600 dark:text-primary-400">
                Built for the rest of us.
              </span>
            </h1>
            <p className="mt-5 text-lg text-gray-600 dark:text-gray-300 max-w-xl">
              Professional tools at individual prices. Real-time prices, technical indicators, DCF valuation, and AI-generated
              analysis on every US stock.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              {NEW_SIGNUPS_OPEN && (
                <Link
                  to="/register"
                  className="px-6 py-3 rounded-lg font-semibold bg-primary-600 hover:bg-primary-700 text-white transition-colors shadow-lg shadow-primary-500/30"
                >
                  Start Free Trial →
                </Link>
              )}
              <button
                onClick={scrollToPricing}
                className="px-6 py-3 rounded-lg font-semibold border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:border-primary-500 dark:hover:border-primary-500 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
              >
                See Pricing
              </button>
            </div>
          </div>
          <div className="md:pl-6">
            <HeroDemoCard />
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Trusted-by strip 
        <div className="mt-8 mb-20 text-center">
          <p className="text-sm uppercase tracking-widest text-gray-500 dark:text-gray-400 font-medium">
            Built in Post Falls, Idaho · Powered by Anthropic · FMP · Stripe · SendGrid
          </p>
        </div>
        */}

        {/* How it works */}
        <div className="mb-32">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight text-gray-900 dark:text-white">
              How it works
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 max-w-5xl mx-auto">
            {[
              { n: '1', title: 'Start a Free Trial', desc: '14 days free on the Beginner and Casual plans.' },
              { n: '2', title: 'Search Any Ticker', desc: 'US stocks, ETFs, and indexes — all in one place.' },
              { n: '3', title: 'Get Institutional-Grade Analysis', desc: 'Charts, indicators, DCF, and AI commentary in seconds.' },
            ].map((s) => (
              <div
                key={s.n}
                className="rounded-2xl p-6 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800"
              >
                <div className="w-10 h-10 rounded-full bg-primary-600 text-white font-bold flex items-center justify-center mb-4">
                  {s.n}
                </div>
                <h3 className="font-bold text-lg mb-1 text-gray-900 dark:text-white">{s.title}</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                  {s.desc}
                </p>
              </div>
            ))}
          </div>
        </div>


        {/* Section heading for deep-dives */}
        <div className="text-center mb-16">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
            Everything You Need to Succeed
          </h2>
          <p className="text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
            Institutional-grade analysis tools designed for retail investors — no Bloomberg terminal required.
          </p>
        </div>

        {/* 3. Live Dashboard & Real-Time Prices */}
        <div className="space-y-24">
          <FeatureSection
            eyebrow="🟢 LIVE"
            eyebrowColor="green"
            title="Live Dashboard & Real-Time Prices"
            body={
              <>
                See prices update the moment the market moves. Your watchlist, portfolio, and dashboard
                stream live via WebSocket — no refreshing required. Pre-market and after-hours quotes are
                tracked separately so you know exactly what's happening before the bell.
              </>
            }
            bullets={[
              'Live WebSocket price streaming for your entire watchlist',
              'Pre-market and after-hours badges with separate % change',
              'Real-time portfolio value with automatic P&L calculations',
              'Sector breakdown and diversification charts',
              'Market status badges — open, closed, or extended hours',
              'Daily top gainers and sector-based stock discovery',
            ]}
            screenshotFile="dashboard-livefeed.png"
            screenshotDescription="Dashboard or Watchlist showing live prices flashing green/red, with at least one ticker showing a PM or AH badge."
            imageSrc="/landing/dashboard-livefeed.png"
            imageAlt="NWC-Analytics live dashboard streaming real-time watchlist prices with pre-market and after-hours badges"
          />

          {/* 4. AI Stock & Portfolio Analysis */}
          <FeatureSection
            eyebrow="✨ AI-POWERED"
            eyebrowColor="purple"
            title="AI Stock & Portfolio Analysis"
            body={
              <>
                Get plain-language analysis of any stock or your entire portfolio, powered by Anthropic's
                Claude. Skip the jargon — understand what's actually happening with your investments and
                what to watch for next. Available on {planNamesWith('aiAnalysis')} tiers.
              </>
            }
            bullets={[
              'Plain-language stock breakdowns — fundamentals, technicals, sentiment',
              'Portfolio-level summaries: concentration, sector tilt, risk highlights',
              'AI-generated price forecasts cached for 6 hours',
              `Usage scales with tier: ${AI_USAGE_RANGE}`,
              'Built on Anthropic Claude — no hallucinated tickers or made-up financials',
            ]}
            screenshotFile="ai-analysis.png"
            screenshotDescription="Stock detail or Dashboard showing the AI-generated analysis text panel for a real ticker."
            imageSrc="/landing/ai-analysis.png"
            imageAlt="NWC-Analytics AI-generated plain-language stock analysis panel powered by Anthropic Claude"
            reverse
            withChrome
          />

          {/* 5. Pro Technical Analysis + Intraday Charting */}
          <FeatureSection
            eyebrow="📈 15+ INDICATORS"
            eyebrowColor="primary"
            title="Pro Technical Analysis"
            body={
              <>
                Go beyond basic charts with the full suite of indicators professional traders use. Each
                comes with clear explanations so you understand exactly what the data is telling you.
              </>
            }
            bullets={[
              'RSI, MACD, Bollinger Bands, Stochastic, ADX, ATR',
              'Moving Averages (SMA/EMA), VWAP, OBV, A/D Line',
              'Ichimoku Cloud, Keltner & Donchian Channels, Parabolic SAR',
              'CCI and Rate of Change momentum indicators',
              'Volatility Squeeze detection — Bollinger inside Keltner, with coiling vs. fired status',
              'Watchlist batch analysis with preset filters (oversold, overbought, uptrend)',
            ]}
            screenshotFile="tech-analysis.png"
            screenshotDescription="TechnicalAnalysis page showing candlestick chart with RSI + MACD subpanels for a real ticker."
            imageSrc="/landing/tech-analysis.png"
            imageAlt="NWC-Analytics technical analysis candlestick chart with RSI and MACD indicator panels"
          />

          <FeatureSection
            eyebrow="🕯️ 1-MINUTE BARS"
            eyebrowColor="amber"
            title="Intraday Charting with Drawing Tools"
            body={
              <>
                Zoom from 1-minute bars out to multi-day views, draw channels and trendlines directly on
                the chart, and overlay moving averages — all without leaving the page.
              </>
            }
            bullets={[
              '1-minute intraday bars (recently upgraded from 15-minute)',
              'Channel and trendline drawing tools',
              'Cursor-draw mode for free-form annotation',
              'Zoom levels 1× through 5× for detail-level analysis',
              '50-day and 200-day moving average overlays on price history',
              'Pre-market and after-hours bars included',
            ]}
            screenshotFile="intraday-drawing.png"
            screenshotDescription="Intraday chart with channel drawing visible, MA overlays on, zoomed in. Capture mid-draw if possible."
            imageSrc="/landing/intraday-drawing.png"
            imageAlt="NWC-Analytics intraday 1-minute chart with trendline drawing tools and moving-average overlays"
            reverse
          />

          {/* 6. DCF Valuation */}
          <FeatureSection
            eyebrow="💰 VALUATION"
            eyebrowColor="purple"
            title="DCF Valuation with Auto-Suggested Inputs"
            body={
              <>
                Run Discounted Cash Flow analyses on any stock to estimate intrinsic value. The system
                auto-suggests growth rates, discount rates, and projection periods based on the company's
                sector, size, and financial profile — or override every assumption yourself.
              </>
            }
            bullets={[
              'AI-suggested growth rate (with conservative 20% haircut by default)',
              'Auto-derived WACC, terminal growth, and projection horizon',
              'Year-by-year projected cash flows and present values',
              'Intrinsic value per share with margin of safety',
              'Clear Buy / Hold / Overvalued recommendation',
              'Cross-checked against independent simple, levered & custom DCF benchmarks',
            ]}
            screenshotFile="dcf-suggestions.png"
            screenshotDescription="DCF page for a recognizable ticker (e.g. MSFT or AAPL) showing the suggestion panel and the resulting Buy/Hold/Overvalued recommendation."
            imageSrc="/landing/dcf-suggestions.png"
            imageAlt="NWC-Analytics DCF valuation with auto-suggested growth and discount rates and a Buy/Hold/Overvalued recommendation"
          />

          {/* 6b. Relative Valuation */}
          <FeatureSection
            eyebrow="⚖️ PEER MULTIPLES"
            eyebrowColor="blue"
            title="Relative Valuation — Peer-Multiple Price Targets"
            body={
              <>
                A DCF tells you what a company has to do to justify its price. Relative valuation tells you
                what the market is already paying for comparable businesses right now — and the gap between
                the two is itself a signal. Build your own peer set, apply median P/E, P/S, and EV/EBITDA
                multiples to forward estimates, and get a defensible target-price range instead of a single
                hero number.
              </>
            }
            bullets={[
              'Three methods — P/E, P/S, EV/EBITDA — combined into a MIN–MAX target range',
              'Build a peer set from the screener or by ticker, then hand-cut bad comps',
              'Median hygiene excludes negative and extreme (>100×) P/E outliers',
              'Forward estimates, net debt, and shares auto-fill with source badges',
              '±10% sensitivity grid shows which assumption your thesis rides on',
              'One-click cross-check against the DCF intrinsic value for the same ticker',
            ]}
            screenshotFile="relative-valuation.png"
            screenshotDescription="Relative Valuation tab showing the MIN–MAX target-price range, the three method cards (P/E, P/S central, EV/EBITDA), and the P/S sensitivity grid."
            imageSrc="/landing/relative-valuation.png"
            imageAlt="NWC-Analytics relative valuation with a peer-multiple price-target range from P/E, P/S and EV/EBITDA methods"
            reverse
          />

          {/* 7. Smart Alerts */}
          <FeatureSection
            eyebrow="🔔 SMART ALERTS"
            eyebrowColor="rose"
            title="Price + Technical Alerts"
            body={
              <>
                Never stare at charts again. Set price alerts for crossings, or wire up technical alerts
                that fire on indicator events — RSI extremes, moving-average crossovers, MACD crosses,
                Bollinger band breaches, sentiment shifts, and Rule-of-40 changes. Delivered to your
                inbox the moment they fire.
              </>
            }
            bullets={[
              'Price alerts: above / below thresholds, monitored during market hours',
              'Technical alerts: RSI extreme, MA crossover, MACD cross',
              'Technical alerts: Bollinger breach, sentiment shift, Rule of 40',
              'Email delivery on every plan',
              `Up to ${TOP_PLAN.limits.priceAlerts} price alerts and ${TOP_PLAN.limits.smartAlerts} technical alerts on ${TOP_PLAN.shortName}`,
            ]}
            screenshotFile="alerts-page.png"
            screenshotDescription="Alerts page showing both a price alert and at least one technical alert configured."
            imageSrc="/landing/alerts-page.png"
            imageAlt="NWC-Analytics alerts page with price alerts and technical indicator alerts configured"
          />

          {/* 8. Stock Screener */}
          <FeatureSection
            eyebrow="🔍 DISCOVERY"
            eyebrowColor="blue"
            title="Stock Screener with Technical Filters"
            body={
              <>
                Find the next setup before the crowd. Filter the market by price, market cap, volume,
                technical patterns, and 52-week range. Spot golden crosses and death crosses, gap-up
                breakouts, and volume spikes — without scrolling through thousands of tickers.
              </>
            }
            bullets={[
              'Filter by price, market cap, volume, and sector',
              'Technical filters: golden cross, death cross, 50/200-day MA position',
              '52-week high/low proximity, gap %, volume spikes',
              'One-click "Volatility Squeeze" and "Squeeze Fired" presets (Bollinger inside Keltner)',
              'Daily snapshot covers all common stocks and ETFs',
              `Save your screens (${planNamesWith('savedScreens')})`,
            ]}
            screenshotFile="screener.png"
            screenshotDescription="Stocks page (screener view) with filters applied — ideally a technical filter like golden cross or 52-week range."
            imageSrc="/landing/screener.png"
            imageAlt="NWC-Analytics stock screener with technical filters such as golden cross and 52-week range applied"
            reverse
          />

          {/* Options Calculator — condensed feature card */}
          <div className="bg-white dark:bg-gray-600 rounded-xl shadow-lg dark:shadow-gray-200/50 border dark:border-gray-300 p-8">
            <div className="grid md:grid-cols-3 gap-6 items-center">
              <div className="md:col-span-2">
                <div className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 mb-3">
                  🧮 Options Calculator
                </div>
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                  Black-Scholes & Binomial Pricing
                </h3>
                <p className="text-gray-600 dark:text-gray-300">
                  Price single-leg and multi-leg strategies, visualize P&L at expiration, and calculate
                  full Greeks (Delta, Gamma, Theta, Vega, Rho) — each with a plain-English definition a
                  click away. Inputs stay grounded in real market data: a live, tenor-matched Treasury
                  risk-free rate and realistic near-the-money strikes. Implied volatility solver included.
                  Available on {planNamesWith('optionsCalculator')} tiers.
                </p>
              </div>
              <div className="flex flex-wrap gap-2 justify-start md:justify-end">
                {['Delta', 'Gamma', 'Theta', 'Vega', 'Rho'].map((g) => (
                  <span
                    key={g}
                    className="px-3 py-1 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 rounded-full text-sm font-medium"
                  >
                    {g}
                  </span>
                ))}
              </div>
            </div>
          </div>

        </div>

        {/* 9. Pricing */}
        <div className="mt-32">
          <PricingTiers />
        </div>

      </div>

      {/* Footer */}
      <footer className="bg-forest-800 dark:bg-gray-950 text-gray-300 dark:text-gray-400 py-12 mt-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="flex items-center mb-4 md:mb-0">
              <img
                src="/images/logo.png"
                alt="NWC-Analytics, LLC"
                className="h-12 w-12 mr-3"
              />
              <div>
                <p className="text-xl font-bold text-white">NWC-Analytics</p>
                <p className="text-sm">Professional stock analysis for retail investors</p>
              </div>
            </div>
            <div className="text-center md:text-right">
              <p className="text-sm">© 2026 NWC-Analytics, LLC. All rights reserved.</p>
              <p className="text-sm mt-1">Post Falls, Idaho</p>
            </div>
          </div>
          <div className="mt-6 flex justify-center flex-wrap gap-x-6 gap-y-2 text-sm">
            <Link to="/terms" className="hover:text-white transition-colors">Terms</Link>
            <Link to="/privacy" className="hover:text-white transition-colors">Privacy</Link>
            <Link to="/faq" className="hover:text-white transition-colors">FAQ</Link>
            <Link to="/blogs" className="hover:text-white transition-colors">Blog</Link>
            <a href="mailto:support@nwc-analytics.com" className="hover:text-white transition-colors">Support</a>
          </div>
        </div>
        <BackToTop />
      </footer>
    </div>
  );
};

export default Landing;
